import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Trash2, Send, FileText, Briefcase } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import SearchableSelect from "@/components/SearchableSelect";
import JobDeliveryAddress from "@/components/JobDeliveryAddress";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

/** Package 35 — the meeting's fix for "reviewing the order only in the
 * side cart is confusing": a full page to review the PO before sending.
 * The flow itself is unchanged: cart → Send for approval or Draft PO. */
export default function OrderReview() {
  const [cart, setCart] = useState(null);
  const [context, setContext] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [codes, setCodes] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [costCodeId, setCostCodeId] = useState("");
  const [addressId, setAddressId] = useState("");
  const [addressFixed, setAddressFixed] = useState(false);
  const [requestedDate, setRequestedDate] = useState("");
  const [notes, setNotes] = useState("");
  const [payBy, setPayBy] = useState("card"); // Package 44 — chosen now, so approval creates the PO directly
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/contractor/cart").then((r) => setCart(r.data));
  useEffect(() => {
    load();
    api.get("/contractor/cart/context").then((r) => setContext(r.data?.context || null)).catch(() => {});
    api.get("/contractor/jobs").then((r) => setJobs(r.data));
    api.get("/contractor/cost-codes").then((r) => setCodes(r.data.filter((c) => c.active !== false)));
  }, []);

  // Delivery goes to the job site or the main office only (Test Drive 2026-10-21).
  useEffect(() => {
    api.get("/contractor/po-delivery-addresses", { params: { job_id: context?.job_id || undefined } }).then((r) => {
      setAddresses(r.data.addresses);
      setAddressFixed(!!r.data.fixed);
      if (r.data.fixed) setAddressId(r.data.preselect_id);
      else if (r.data.preselect_id) setAddressId((prev) => prev || r.data.preselect_id);
    });
  }, [context?.job_id]);

  const byVendor = useMemo(() => {
    if (!cart) return [];
    const groups = {};
    for (const i of cart) (groups[i.vendor_name || "Vendor"] ||= []).push(i);
    return Object.entries(groups);
  }, [cart]);

  const total = useMemo(() => (cart || []).reduce((s, i) => s + i.qty * i.unit_price, 0), [cart]);

  const contextLabel = useMemo(() => {
    if (!context) return "No shopping context pinned";
    if (context.type === "job") {
      const j = jobs.find((j) => j.id === context.job_id);
      return j ? `${j.code} — ${j.name}` : "Job";
    }
    if (context.type === "change_order") return "Change order";
    return "Non-job context";
  }, [context, jobs]);

  const updateQty = async (item, qty) => {
    if (!qty || qty <= 0) return;
    try {
      await api.patch(`/contractor/cart/${item.id}`, { qty: Number(qty) });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Quantity not allowed");
      load();
    }
  };

  const removeItem = async (item) => {
    await api.delete(`/contractor/cart/${item.id}`);
    load();
  };

  const submit = async (mode) => {
    if (!addressId) return toast.error("Select a delivery address");
    setBusy(true);
    try {
      await api.post("/contractor/cart/checkout", {
        cost_code_id: costCodeId || null, delivery_address_id: addressId, mode,
        vendor_notes: notes || null, expected_delivery_date: requestedDate || null,
        payment_method: mode === "pr" ? payBy : undefined,
      });
      toast.success(mode === "po" ? "Draft PO(s) created" : "Order sent for approval — it becomes a PO once approved");
      navigate("/contractor/purchase-orders");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    } finally {
      setBusy(false);
    }
  };

  if (!cart) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="order-review-page">
      <Link to="/contractor/catalog" className="flex items-center gap-1 text-[13px] text-slate-500 hover:text-slate-800"><ChevronLeft size={15} /> Back to Catalog</Link>
      <h1 className="mt-3 text-2xl font-extrabold tracking-tight">Review Order</h1>
      <p className="text-slate-500 text-sm mt-1">Check everything before it goes out — the full order on one page, not a side panel.</p>

      <div className="mt-4 flex items-center gap-2 rounded-lg bg-slate-900 text-white px-3.5 py-2.5 text-[12.5px]" data-testid="review-context-chip">
        <Briefcase size={14} className="text-sky-300" />
        <span className="font-semibold">Ordering for: {contextLabel}</span>
        <Link to="/contractor/catalog" className="ml-auto text-sky-300 hover:text-sky-200 font-semibold">change</Link>
      </div>

      {cart.length === 0 ? (
        <div className="mt-8 py-12 text-center text-slate-400 text-sm">Your cart is empty — add products from the catalog first.</div>
      ) : (
        <>
          {byVendor.map(([vendorName, items]) => (
            <div key={vendorName} className="mt-5 bg-white rounded-xl border border-slate-100 p-5" data-testid={`review-vendor-${vendorName.replace(/[^a-zA-Z0-9]/g, "_")}`}>
              <div className="font-bold text-[14px]">{vendorName}</div>
              <div className="text-[11.5px] text-slate-400">Becomes its own purchase order</div>
              <table className="w-full mt-3 text-[13px]">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
                    <th className="text-left py-2">Item</th><th className="text-right py-2 w-28">Qty</th><th className="text-right py-2 w-28">Unit Price</th><th className="text-right py-2 w-28">Total</th><th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => (
                    <tr key={i.id} className="border-b border-slate-50" data-testid={`review-line-${i.id}`}>
                      <td className="py-2.5 pr-2">{i.product_name}</td>
                      <td className="py-2.5 text-right">
                        <input type="number" min="1" step="any" defaultValue={i.qty} onBlur={(e) => Number(e.target.value) !== i.qty && updateQty(i, e.target.value)}
                          data-testid={`review-qty-${i.id}`} className="w-20 h-9 px-2 rounded-lg border border-slate-200 text-right text-[13px]" />
                      </td>
                      <td className="py-2.5 text-right mono">{fmt(i.unit_price)}</td>
                      <td className="py-2.5 text-right mono font-medium">{fmt(i.qty * i.unit_price)}</td>
                      <td className="py-2.5 text-right"><button onClick={() => removeItem(i)} data-testid={`review-remove-${i.id}`} className="text-slate-300 hover:text-red-500"><Trash2 size={15} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-100 p-4 space-y-3">
              <div>
                <label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Delivery address *</label>
                {addressFixed ? (
                  <JobDeliveryAddress address={addresses[0]} jobId={context?.job_id} testId="review-job-delivery-address" />
                ) : (
                <SearchableSelect value={addressId} onChange={(e) => setAddressId(e.target.value)} data-testid="review-address-select" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-[13px]">
                  <option value="">Select address…</option>
                  {addresses.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                </SearchableSelect>
                )}
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Requested delivery date</label>
                <input type="date" value={requestedDate} onChange={(e) => setRequestedDate(e.target.value)} data-testid="review-date-input" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-[13px]" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Cost code (optional — assigned per line at invoice review)</label>
                <SearchableSelect value={costCodeId} onChange={(e) => setCostCodeId(e.target.value)} data-testid="review-costcode-select" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-[13px]">
                  <option value="">None yet</option>
                  {codes.map((c) => <option key={c.id} value={c.id}>{c.code} {c.name}</option>)}
                </SearchableSelect>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Pay by</label>
                <div className="mt-1 flex gap-2" data-testid="review-pay-by">
                  {[["card", "Card"], ["on_account", "On account"], ["ramp", "Ramp"]].map(([v, label]) => (
                    <button key={v} type="button" onClick={() => setPayBy(v)} data-testid={`review-pay-${v}`}
                      className={`flex-1 h-10 rounded-lg text-[12.5px] font-semibold ${payBy === v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{label}</button>
                  ))}
                </div>
                {payBy === "on_account" && <p className="text-[11.5px] text-slate-400 mt-1">Only for vendors that give your company on-account credit.</p>}
                {payBy === "ramp" && <p className="text-[11.5px] text-slate-400 mt-1">Paid with a Ramp card. It posts to your Ramp account in QuickBooks, not as a bill.</p>}
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Notes to vendor</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="review-notes-input" className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px]" />
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 p-4 flex flex-col">
              <div className="flex justify-between text-[14px] font-bold"><span>Order total</span><span className="mono" data-testid="review-total">{fmt(total)}</span></div>
              <div className="text-[11.5px] text-slate-400 mt-1">{cart.length} line{cart.length === 1 ? "" : "s"} across {byVendor.length} vendor{byVendor.length === 1 ? "" : "s"} · tax added per company default at submission</div>
              <div className="mt-auto pt-4 space-y-2">
                <button onClick={() => submit("pr")} disabled={busy} data-testid="review-send-approval-button" className="w-full h-11 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-bold flex items-center justify-center gap-2 disabled:opacity-50">
                  <Send size={14} /> Send for Approval
                </button>
                <button onClick={() => submit("po")} disabled={busy} data-testid="review-draft-po-button" className="w-full h-11 rounded-lg border border-slate-200 text-[13px] font-semibold flex items-center justify-center gap-2 hover:bg-slate-50 disabled:opacity-50">
                  <FileText size={14} /> Save as Draft PO
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
